import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Mic, Send, Loader2, Sparkles, ArrowLeft, Award, TrendingUp, MessageCircle, RefreshCw, Lightbulb, CheckCircle2,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import WeKitLogo from "@/components/WeKitLogo";
import { cloudStorage } from "@/lib/cloudStore";

interface Question { text: string; competency: string; hint: string }
interface Feedback {
  overall_score: number;
  one_line_verdict: string;
  strengths: string[];
  improvements: string[];
  per_question: { score: number; feedback: string; model_answer_snippet: string }[];
  scores: { communication: number; depth: number; confidence: number; structure: number };
  next_step: string;
}

const COMPETENCY_COLOR: Record<string, string> = {
  motivation: "bg-rose-500/10 text-rose-600 border-rose-500/30",
  behavioral: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  technical: "bg-violet-500/10 text-violet-600 border-violet-500/30",
  situational: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  values: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
};

export default function Interview() {
  const navigate = useNavigate();
  const presetCareer = useMemo(() => cloudStorage.getItem("wekit_interview_career") ?? "", []);
  const [career, setCareer] = useState(presetCareer);
  const [phase, setPhase] = useState<"setup" | "interview" | "scoring" | "done">("setup");
  const [starting, setStarting] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [intro, setIntro] = useState("");
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<string[]>([]);
  const [idx, setIdx] = useState(0);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const start = async () => {
    if (!career.trim()) {
      toast({ title: "Pick a career", description: "Type a role you want to interview for." });
      return;
    }
    setStarting(true);
    try {
      const { data, error } = await supabase.functions.invoke("mock-interview", {
        body: {
          action: "start",
          career_title: career.trim(),
          assessment_id: cloudStorage.getItem("wekit_assessment_id") || undefined,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setSessionId(data.session_id);
      setIntro(data.intro);
      setQuestions(data.questions);
      setAnswers(new Array(data.questions.length).fill(""));
      setIdx(0);
      setPhase("interview");
    } catch (e: any) {
      toast({ title: "Couldn't start", description: e.message, variant: "destructive" });
    } finally {
      setStarting(false);
    }
  };

  const submit = async () => {
    setPhase("scoring");
    try {
      const transcript = questions.map((q, i) => ({ question: q.text, answer: answers[i] ?? "" }));
      const { data, error } = await supabase.functions.invoke("mock-interview", {
        body: { action: "score", session_id: sessionId, transcript },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setFeedback(data);
      setPhase("done");
    } catch (e: any) {
      toast({ title: "Couldn't score", description: e.message, variant: "destructive" });
      setPhase("interview");
    }
  };

  const reset = () => {
    setPhase("setup");
    setSessionId(null);
    setQuestions([]);
    setAnswers([]);
    setIdx(0);
    setFeedback(null);
    setIntro("");
  };

  const progress = questions.length ? Math.round(((idx + 1) / questions.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="container max-w-3xl flex items-center justify-between py-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <WeKitLogo className="h-7" />
          <div className="w-16" />
        </div>
      </header>

      <main className="container max-w-3xl py-8 space-y-6">
        {phase === "setup" && (
          <Card className="border-primary/20 bg-gradient-to-br from-primary/5 via-background to-accent/5">
            <CardHeader>
              <CardTitle className="font-display text-2xl flex items-center gap-2">
                <Mic className="h-6 w-6 text-primary" /> AI Mock Interview
              </CardTitle>
              <p className="text-muted-foreground text-sm mt-2">
                Practice 5 realistic interview questions for your dream role. YOMA scores you and gives feedback.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground">Which career?</label>
                <Input
                  value={career}
                  onChange={(e) => setCareer(e.target.value)}
                  placeholder="e.g. Product Designer, Data Scientist…"
                  className="mt-2"
                />
              </div>
              <Button onClick={start} disabled={starting} size="lg" className="w-full rounded-full gap-2 bg-gradient-to-r from-primary to-accent">
                {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Start Interview
              </Button>
            </CardContent>
          </Card>
        )}

        {phase === "interview" && questions[idx] && (
          <>
            {idx === 0 && intro && (
              <Card className="border-border/50 bg-card/50">
                <CardContent className="p-5 text-sm text-foreground italic">"{intro}"</CardContent>
              </Card>
            )}

            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Question {idx + 1} of {questions.length}</span>
              <span>{progress}%</span>
            </div>
            <Progress value={progress} className="h-1.5" />

            <Card className="border-primary/20">
              <CardHeader>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <Badge variant="outline" className={`text-[10px] rounded-full capitalize ${COMPETENCY_COLOR[questions[idx].competency] ?? ""}`}>
                    {questions[idx].competency}
                  </Badge>
                </div>
                <CardTitle className="font-display text-xl leading-snug pt-2">
                  {questions[idx].text}
                </CardTitle>
                <p className="text-xs text-muted-foreground flex items-start gap-1.5 mt-2">
                  <Lightbulb className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
                  {questions[idx].hint}
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <Textarea
                  value={answers[idx] ?? ""}
                  onChange={(e) => {
                    const next = [...answers];
                    next[idx] = e.target.value;
                    setAnswers(next);
                  }}
                  placeholder="Type your answer here. Be specific and use examples…"
                  className="min-h-[160px] resize-y"
                />
                <div className="flex gap-2 justify-between">
                  <Button
                    variant="outline"
                    onClick={() => setIdx((i) => Math.max(0, i - 1))}
                    disabled={idx === 0}
                    className="rounded-full"
                  >
                    Previous
                  </Button>
                  {idx < questions.length - 1 ? (
                    <Button
                      onClick={() => setIdx((i) => i + 1)}
                      className="rounded-full gap-2 bg-gradient-to-r from-primary to-accent"
                    >
                      Next <Send className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      onClick={submit}
                      className="rounded-full gap-2 bg-gradient-to-r from-primary to-accent"
                    >
                      Submit & Score <Sparkles className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {phase === "scoring" && (
          <Card className="border-border/50">
            <CardContent className="p-12 flex flex-col items-center gap-3">
              <Loader2 className="h-7 w-7 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">YOMA is reviewing your interview…</p>
            </CardContent>
          </Card>
        )}

        {phase === "done" && feedback && (
          <>
            <Card className="border-primary/30 bg-gradient-to-br from-primary/10 via-background to-accent/10">
              <CardContent className="p-8 text-center">
                <Award className="mx-auto h-10 w-10 text-primary" />
                <p className="mt-2 text-xs uppercase tracking-wide text-muted-foreground">Overall Score</p>
                <p className="mt-1 font-display text-5xl font-bold gradient-text">{feedback.overall_score}</p>
                <p className="mt-3 text-foreground font-medium">{feedback.one_line_verdict}</p>
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(feedback.scores).map(([k, v]) => (
                <Card key={k} className="border-border/50">
                  <CardContent className="p-4 text-center">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{k}</p>
                    <p className="mt-1 font-display text-2xl font-bold text-foreground">{v}</p>
                    <Progress value={v} className="h-1 mt-2" />
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Card className="border-emerald-500/30 bg-emerald-500/5">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2 text-emerald-700">
                    <CheckCircle2 className="h-4 w-4" /> Strengths
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    {feedback.strengths.map((s, i) => <li key={i} className="flex gap-2"><span className="text-emerald-600">+</span>{s}</li>)}
                  </ul>
                </CardContent>
              </Card>
              <Card className="border-amber-500/30 bg-amber-500/5">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2 text-amber-700">
                    <TrendingUp className="h-4 w-4" /> To improve
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    {feedback.improvements.map((s, i) => <li key={i} className="flex gap-2"><span className="text-amber-600">→</span>{s}</li>)}
                  </ul>
                </CardContent>
              </Card>
            </div>

            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="text-lg font-display flex items-center gap-2">
                  <MessageCircle className="h-5 w-5 text-primary" /> Question-by-question feedback
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {feedback.per_question.map((p, i) => (
                  <div key={i} className="rounded-xl border border-border/50 bg-card/50 p-4">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <p className="text-xs text-muted-foreground">Q{i + 1}: {questions[i]?.text}</p>
                      <Badge className="bg-primary/10 text-primary border-primary/20">{p.score}/100</Badge>
                    </div>
                    <p className="text-sm text-foreground">{p.feedback}</p>
                    <div className="mt-3 rounded-lg bg-primary/5 border border-primary/20 p-3">
                      <p className="text-[10px] uppercase tracking-wide text-primary font-medium">Stronger answer</p>
                      <p className="text-sm text-foreground mt-1 italic">{p.model_answer_snippet}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="border-primary/30 bg-primary/5">
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-wide text-primary font-medium">Next step</p>
                <p className="mt-1 text-foreground">{feedback.next_step}</p>
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button onClick={reset} variant="outline" className="flex-1 rounded-full gap-2">
                <RefreshCw className="h-4 w-4" /> Try another
              </Button>
              <Button onClick={() => navigate("/report")} className="flex-1 rounded-full bg-gradient-to-r from-primary to-accent">
                Back to Report
              </Button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
